import { CommonDialogs } from "@mcleod/common";
import {
    DataSourceMode,
    EditRowDecorator,
    TableRow, TableRowBeforeSaveEvent, TableRowCreationEvent,
    TableRowDisplayEvent,
    TableRowMode, TableRowMoveEvent,
    Textbox,
    ValidationResult
} from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { Contact } from "@mcleod/general/src/Contact";
import { AutogenLayoutMobileCommControlCredentialsTable } from "./autogen/AutogenLayoutMobileCommControlCredentialsTable";

export class MobileCommControlCredentialsTable extends AutogenLayoutMobileCommControlCredentialsTable {

    public _minLength: number;
    private _minSpecials: number;
    private _minUppers: number;
    private _minNumeric: number;
    private _specialAllowed: String;

    override async onLoad() {
        await Api.search("users/password/requirements", { appendPrefix: false }).then(response => {
            this._minLength = response.data[0].password_length;
            this._minSpecials = response.data[0].special_chars;
            this._minUppers = response.data[0].upper_chars;
            this._minNumeric = response.data[0].numeric_chars;
            this._specialAllowed = response.data[0].allowed_special_chars;
        })

        this.tableContacts.onEditLayoutLoaded = this.configureEditLayout();

        this.tableContacts.addRowCreateListener((event: TableRowCreationEvent) => {
            const row = event.getTableRow();
            if (row.mode === TableRowMode.QUICK_ADD) {
                row.data.set("web_access", "Y");
            }
        });
    }

    configureEditLayout(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return async (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const contactLayout = rowDecorator.layout as Contact;
            rowDecorator.width = "60%";
            rowDecorator.multiButton.dropdownItems = null;
            const modelRow = tableRow.data as ModelRow;

            const webUserId = modelRow?.getString("users.id", null);
            const loginId = modelRow?.getString("users.login_id", null);
            const webAccess = modelRow?.get("web_access", "N") == "Y";
            contactLayout.textboxWebUserId.text = contactLayout.switchWebAccess.checked ? webUserId : null;
            contactLayout.textboxLoginId.text = contactLayout.switchWebAccess.checked ? loginId : null;
            contactLayout.buttonViewUser.enabled = webAccess && !modelRow.hasChanged();
            if (modelRow.hasChanged()) {
                contactLayout.buttonViewUser.tooltip = "Please save updates to this record before viewing web user";
            }
            contactLayout.buttonViewUser.color = contactLayout.buttonViewUser.enabled ? "primary" : "stroke.secondary";
            contactLayout.switchPrimaryContact.displayData(modelRow, null, 0);

            rowDecorator.onValidate = (updatedData: ModelRow): ValidationResult[] => {
                if (updatedData.get("is_active") == "N" && updatedData.get("is_primary_contact") == "Y") {
                    CommonDialogs.showError("The primary contact must be active");
                    return [{ isValid: false, component: contactLayout.switchIsActive }]
                }
                return [{ isValid: true, component: contactLayout.switchIsActive }]
            }

            rowDecorator.onClose = ((cancelled: boolean) => {
                rowDecorator.slideOut().then(() => {
                    if (!cancelled) {
                        this.sourceContacts.displayDataInBoundComponents();
                    }
                });
            });
            rowDecorator.onSave = (async (updatedData: ModelRow) => {
                if (updatedData.getBoolean("is_primary_contact", false)) {
                    this.sourceContacts.data.forEach(row => {
                        if (row.getString("id") !== updatedData.getString("id")) {
                            row.set("is_primary_contact", false);
                        }
                    });
                }
                tableRow.saveChangesFromEdit(updatedData);
            });
        };
    }

    tableContactsAfterTableRowMove(event: TableRowMoveEvent) {
        const primaryContactRow = this.tableContacts.rows.find(row => row.data.getBoolean("is_primary_contact", false));
        if (primaryContactRow != null && primaryContactRow.index !== 0) {
            this.tableContacts.moveRow(primaryContactRow, 0);
        }
    }

    set parentRowType(parentRowType: string) {

        if (parentRowType === "S") {
            const columns = ["Name", "Title", "Web Access"];
            columns.forEach(this.hideColumn, this);

            this.tableContacts.addRowBeforeSaveListener((event: TableRowBeforeSaveEvent) => {
                const textboxName = event.getTableRow().findComponentById("textboxName")
                if (textboxName) {
                    textboxName.required = false;
                }
                const data = event.getTableRow().data;
                data.set("name", event.getDataSource().parentDataSource.activeRow.getString("name"));
                data.set("parent_row_type", "S");
            });
        }
    }

    private hideColumn(columnCaption: string) {
        const column = this.tableContacts.getColumnByCaption(columnCaption);
        this.tableContacts.setColumnVisibility(column, false);
    }

    tableContactsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow()
        const textboxWebPassword = tableRow.findComponentById("textboxWebPassword") as Textbox

        // Add blur listener for rows to validate password
        if (textboxWebPassword) {
            textboxWebPassword.addChangeListener((event) => {
                if (!StringUtil.isEmptyString(event.oldValue) && event.oldValue !== event.newValue) {
                    textboxWebPassword.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                        return this.getPasswordValidationMessage(textboxWebPassword, mode);
                    };
                }
            });
        }
    }

    /**   POSSIBLY MOVE THESE FUNCTIONS TO A UTILITY CLASS */

    /**
     * Validates a password string and returns validation message if invalid, null if valid
     * This method can be called by other classes for password validation
     */
    public getPasswordValidationMessage(textbox: Textbox, mode: DataSourceMode): ValidationResult {

        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const result: ValidationResult = {
            component: textbox,
            isValid: true
        }

        const password = textbox.text || "";

        if (password.length < this._minLength) {
            result.isValid = false;
            result.validationMessage = "Password must have a length of at least " + this._minLength + " characters.";
        }
        else if (!this.meetsUpperCaseCount(password)) {
            result.isValid = false;
            result.validationMessage = "Password must have at least " + this._minUppers + " upper case character(s).";
        }
        else if (!this.meetsNumericCount(password)) {
            result.isValid = false;
            result.validationMessage = "Password must have at least " + this._minNumeric + " numeric character(s).";
        }
        else if (!this.meetsSpecialCharCount(password)) {
            result.isValid = false;
            result.validationMessage = "Password must have at least " + this._minSpecials + " special character(s). Special characters allowed are: " + this._specialAllowed;
        }
        else if (!this.isSpecialCharsValid(password)) {
            result.isValid = false;
            result.validationMessage = "Password contains invalid special characters. Special characters allowed are: " + this._specialAllowed;
        }

        return result;
    }

    meetsUpperCaseCount(password?: string): boolean {
        const str = password;
        if (str.length > 0) {
            let count = 0;
            for (let i = 0; i < str.length; i++) {
                if (str[i] >= 'A' && str[i] <= 'Z') {
                    count++;
                }
            }
            if (count >= this._minUppers)
                return true;
        }
        return false;
    }

    meetsNumericCount(password?: string): boolean {
        const str = password;
        if (str.length > 0) {
            let count = 0;
            for (let i = 0; i < str.length; i++) {
                if (str[i] >= '0' && str[i] <= '9') {
                    count++;
                }
            }
            if (count >= this._minNumeric)
                return true;
        }
        return false;
    }

    meetsSpecialCharCount(password?: string): boolean {
        const str = password;
        if (str.length > 0) {
            const allowedSpecialChars = this._specialAllowed;
            let count = 0;
            for (let i = 0; i < str.length; i++) {
                if (allowedSpecialChars.includes(str[i])) {
                    count++;
                }
            }
            if (count >= this._minSpecials)
                return true;
        }
        return false;
    }

    isSpecialCharsValid(password?: string): boolean {
        const str = password;
        if (str.length > 0) {
            const allowedSpecialChars = this._specialAllowed;
            for (let i = 0; i < str.length; i++) {
                const char = str[i];
                if (!(char >= 'A' && char <= 'Z') &&
                        !(char >= 'a' && char <= 'z') &&
                        !(char >= '0' && char <= '9') &&
                        !allowedSpecialChars.includes(char)) {
                    return false;
                }
            }
        }
        return true;
    }
}
